import React, {useEffect} from "react";
import {AnimatePresence, motion} from "framer-motion";
import {MAINTENANCE_CONTACT_EMAIL} from "../../feature_flags";

const MAILTO = `mailto:${MAINTENANCE_CONTACT_EMAIL}?subject=${encodeURIComponent("LoA access request")}`;

/**
 * Shared copy for the "we're under maintenance" notice so the landing-page
 * modal and the /app/* full-page notice say exactly the same thing.
 */
export const MaintenanceCopy: React.FC = () => (
    <>
        <p className="lp-mono">Status</p>
        <h2 className="lp-serif mt-3 text-[34px] leading-[1.05] text-[var(--ink)]">
            LoA is under maintenance.
        </h2>
        <p className="mt-4 text-[16px] leading-[1.6] text-[var(--ink-2)]">
            The app is temporarily offline while we work on the infrastructure. If you&apos;d like
            to try LoA in the meantime, email us and we&apos;ll set you up with access directly.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href={MAILTO} className="lp-btn lp-btn-primary">
                Email {MAINTENANCE_CONTACT_EMAIL}
            </a>
        </div>
    </>
);

/** Modal shown when a visitor clicks a "Request access" / "Try it out" CTA. */
export const MaintenanceModal: React.FC<{open: boolean; onClose: () => void}> = ({open, onClose}) => {
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", handler);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.18}}
                    className="lp fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
                    onClick={onClose}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="maintenance-title"
                >
                    <div className="absolute inset-0 bg-[rgba(20,20,20,0.45)]"/>
                    <motion.div
                        initial={{y: 12, opacity: 0}}
                        animate={{y: 0, opacity: 1}}
                        exit={{y: 12, opacity: 0}}
                        transition={{duration: 0.22, ease: [0.2, 0.8, 0.2, 1]}}
                        className="relative w-full max-w-[520px] rounded-[14px] border border-[var(--line)] bg-[var(--paper)] p-8 sm:p-10 shadow-[0_24px_64px_-24px_rgba(20,20,20,0.35)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={onClose}
                            aria-label="Close"
                            className="absolute top-4 right-4 h-9 w-9 rounded-full text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--line)] transition flex items-center justify-center"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                        <div id="maintenance-title">
                            <MaintenanceCopy/>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
